// src/pages/CompanyAdminUser/CompanyAdminUserPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Container, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import PaginatedTable from "../Common/PaginatedTable";
import EntityDetailsSidebar from "../../components/Common/EntityDetailsSidebar";
import AlertMessage from "../../components/Common/AlertMessage";
import extractApiErrors from "../../utils/extractApiErrors";
import { formatDateTime } from "../../utils/formatters";
import {
  getCompanyAdminUsers,
  deleteSecurityUser,
  updateSecurityUser,
} from "../../services/securityUserService";
import {
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Eye,
  UserRound,
} from "lucide-react";

const CompanyAdminUserPage = () => {
  const navigate = useNavigate();

  // Tabla y filtros
  const [users, setUsers] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // UI estados
  const [isLoading, setIsLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]); // errores API (carga/eliminar/actualizar)
  const [successMessage, setSuccessMessage] = useState(null); // mensajes cortos de éxito

  // Sidebar
  const [selectedUser, setSelectedUser] = useState(null);

  // Esquema del sidebar
  const schema = [
    { label: "Empresa", key: "companyName" },
    {
      label: "Creado",
      key: "createdAt",
      render: (value) => formatDateTime(value),
    },
    {
      label: "Último cambio de clave",
      key: "lastPasswordChange",
      render: (value) => (value ? formatDateTime(value) : "No se ha cambiado"),
    },
  ];

  const handleViewDetails = (user) => setSelectedUser(user);

  // Carga paginada + manejo uniforme de errores de API
  const fetchUsers = useCallback(async (page, size, term = "") => {
    setIsLoading(true);
    setSubmitErrors([]); // limpiamos errores anteriores al recargar
    setSuccessMessage(null);

    try {
      const resp = await getCompanyAdminUsers(page, size, term);
      if (!resp?.success) {
        // Esto permite a extractApiErrors entender el formato
        throw { backend: resp };
      }

      setUsers(resp.data.items || []);
      setTotalRows(resp.data.totalCount || 0);
    } catch (err) {
      const parsed = extractApiErrors(err);
      setSubmitErrors(parsed);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Primera carga + cuando cambia paginación/búsqueda
  useEffect(() => {
    const t = setTimeout(() => {
      fetchUsers(currentPage, perPage, searchTerm);
    }, 300); // debounce básico
    return () => clearTimeout(t);
  }, [fetchUsers, currentPage, perPage, searchTerm]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleEdit = (id) => {
    navigate(`/dashboard/company-admin-user/edit/${id}`);
  };

  // Eliminar con confirmación + manejo uniforme de errores
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará al usuario administrador.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    setSubmitErrors([]);
    setSuccessMessage(null);

    try {
      const resp = await deleteSecurityUser(id);
      if (!resp?.success) {
        // MUY IMPORTANTE: lanzar con { backend: resp } para que extractApiErrors funcione
        throw { backend: resp };
      }

      setSuccessMessage("✅ El usuario ha sido eliminado.");
      // refresca tabla manteniendo paginación/búsqueda
      fetchUsers(currentPage, perPage, searchTerm);
    } catch (err) {
      const parsed = extractApiErrors(err);
      setSubmitErrors(parsed);
    }
  };

  // Toggle activo/inactivo con mismo patrón
  const handleToggleActive = async (row) => {
    const confirm = await Swal.fire({
      title: row.isActive
        ? "¿Deseas inactivar este usuario?"
        : "¿Deseas activar este usuario?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, confirmar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setSubmitErrors([]);
    setSuccessMessage(null);

    try {
      const updated = {
        id: row.id,
        fullName: row.fullName,
        email: row.email,
        isActive: !row.isActive,
        companyId: row.companyId,
        roleIds: row.roleIds,
      };

      const resp = await updateSecurityUser(updated);
      if (!resp?.success) {
        throw { backend: resp };
      }

      setSuccessMessage("✅ Estado actualizado correctamente.");
      fetchUsers(currentPage, perPage, searchTerm);
    } catch (err) {
      const parsed = extractApiErrors(err);
      setSubmitErrors(parsed);
    }
  };

  const columns = [
    {
      name: "Nombre",
      selector: (row) => row.fullName,
      sortable: true,
      wrap: true,
    },
    {
      name: "Correo",
      selector: (row) => row.email,
      wrap: true,
    },
    {
      name: "Empresa",
      selector: (row) => row.companyName,
      wrap: true,
    },
    {
      name: "Activo",
      center: true,
      cell: (row) => (
        <Button
          variant="light"
          size="sm"
          className="d-flex align-items-center gap-1 border-0"
          title={row.isActive ? "Desactivar usuario" : "Activar usuario"}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleActive(row);
          }}
        >
          {row.isActive ? (
            <>
              <CheckCircle size={16} className="text-success" /> Activo
            </>
          ) : (
            <>
              <XCircle size={16} className="text-danger" /> Inactivo
            </>
          )}
        </Button>
      ),
      wrap: true,
      width: "150px",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row.id);
            }}
            title="Editar"
          >
            <Pencil size={16} />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row.id);
            }}
            title="Eliminar"
          >
            <Trash2 size={16} />
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      wrap: true,
      width: "150px",
      center: true,
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark">Administradores de empresa</h4>
          <Button
            onClick={() => navigate("/dashboard/company-admin-user/create")}
          >
            + Crear nuevo usuario
          </Button>
        </div>

        {/* Bloque de alertas unificado (errores de API / éxito) */}
        {submitErrors.length > 0 && (
          <AlertMessage
            type="error"
            onClose={() => setSubmitErrors([])}
            message={
              <ul className="mb-0">
                {submitErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            }
          />
        )}

        {successMessage && (
          <AlertMessage
            type="success"
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={users}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
          isLoading={isLoading}
        />

        {isLoading && (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Cargando...</span>
          </div>
        )}
      </Card>

      <EntityDetailsSidebar
        isVisible={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        title="Detalles del usuario"
        icon={<UserRound size={20} />}
        data={selectedUser}
        schema={schema}
      />
    </div>
  );
};

export default CompanyAdminUserPage;
